import isObject from '@/utils/isObject';
import { parseQuery, type QueryInput } from '@/utils/queryParams';

/** An object of params to merge in, a query string to parse, or null/undefined to clear all. */
export type QueryParamsInput = object | string | null | undefined;

type QueryValue = string | number | boolean | null | undefined;

function isQueryValue(value: unknown): value is QueryValue {
    return (
        value === null ||
        value === undefined ||
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
    );
}

/**
 * stringifyQuery writes any other value through `String()`, so converting here keeps the query
 * string it produces unchanged while giving it the type it declares.
 */
function toQueryValue(value: unknown): QueryValue | QueryValue[] {
    if (Array.isArray(value)) {
        return value.map((item: unknown) => (isQueryValue(item) ? item : String(item)));
    }
    return isQueryValue(value) ? value : String(value);
}

// const xxx = mergeQueryParams('?x=1&y=2', {x: null, z: 3}); => {y: 2, z: 3}
// null/undefined will remove, do stringifyQuery(xxx) for string
function mergeQueryParams(...params: QueryParamsInput[]): QueryInput {
    // lets support both object and string params
    const normalized = params.reduce<Record<string, unknown>>((acc, param) => {
        // if we encounter param as pure null/undefined we unset all params
        if (param === null || param === undefined) return {};

        if (isObject(param)) return { ...acc, ...param };
        return { ...acc, ...parseQuery(String(param)) };
    }, {});

    return Object.entries(normalized).reduce<QueryInput>((acc, [key, value]) => {
        if (value === null || value === undefined) {
            delete acc[key];
        } else {
            acc[key] = toQueryValue(value);
        }
        return acc;
    }, {});
}

export default mergeQueryParams;
